import { z } from 'zod';
import { OrderType, OrderSide, AmountType, MarginMode, PriceType, LimitPriceType, PriceReference } from '../types/preset';
import { presetService } from '../services/presetService';

// Define Zod enums matching your TypeScript enums from ../types/preset
const OrderTypeEnum = z.enum(["market", "limit", "stop_market", "stop_limit", "layered", "cancel_all", "cancel_bids", "cancel_asks", "unknown"]);
const OrderSideEnum = z.enum(["buy", "sell"]);
const AmountTypeEnum = z.enum(["percent", "base", "quote"]);
const MarginModeEnum = z.enum(["cross", "isolated"]);
const PriceTypeEnum = z.enum(["ABSOLUTE", "RELATIVE", "PERCENT", "BEST_BID", "BEST_ASK"]);
const PriceReferenceEnum = z.enum(["MARK", "INDEX", "LAST", "MID"]);

/**
 * Note on numeric field validation:
 * 
 * Numeric fields (amount, leverage, prices) accept both:
 * - Numbers (from direct user input in forms)
 * - Strings that can be converted to numbers (from webhook payloads)
 * 
 * This is to maintain compatibility with the backend WebhookPresetPayload schema,
 * which accepts these values as strings.
 */

// Helper function to create a schema that accepts both string and number
export const numberOrStringNumber = (options: { min?: number; max?: number; errorMessage?: string; }) => {
  return z.union([
    z.string().refine(val => {
      const num = Number(val);
      return !isNaN(num) && 
             (options.min === undefined || num >= options.min) && 
             (options.max === undefined || num <= options.max);
    }, { message: options.errorMessage || "Must be a valid number" })
    .transform(val => Number(val)),
    z.number()
      .refine(val => 
        (options.min === undefined || val >= options.min) && 
        (options.max === undefined || val <= options.max),
        { message: options.errorMessage || "Value out of range" }
      )
  ]);
};

// Schema for nested objects
const OrderFlagsSchema = z.object({
  reduce_only: z.boolean(),
  post_only: z.boolean(),
}).optional();

const TakeProfitSettingsSchema = z.object({
  enabled: z.boolean(),
  trigger_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Trigger price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  price_type: PriceTypeEnum.nullable().optional(),
  price_offset: z.union([
    z.string()
      .refine(val => val === '' || !isNaN(Number(val)), { 
        message: "Price offset must be a number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().nullable().optional(),
    z.null().optional()
  ]),
  price_reference: PriceReferenceEnum.nullable().optional(),
  limit_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Limit price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  limit_price_type: PriceTypeEnum.nullable().optional(),
});

const StopLossSettingsSchema = z.object({
  enabled: z.boolean(),
  trigger_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Trigger price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  price_type: PriceTypeEnum.nullable().optional(),
  price_offset: z.union([
    z.string()
      .refine(val => val === '' || !isNaN(Number(val)), { 
        message: "Price offset must be a number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().nullable().optional(),
    z.null().optional()
  ]),
  price_reference: PriceReferenceEnum.nullable().optional(),
  limit_price_value: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Limit price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  limit_price_type: PriceTypeEnum.nullable().optional(),
});

const LayeredOrderSettingsSchema = z.object({
    parts: numberOrStringNumber({ 
      min: 2, 
      max: 50, 
      errorMessage: "Parts must be between 2 and 50" 
    }).transform(val => Math.floor(Number(val))), // Ensure integer
    step: numberOrStringNumber({ 
      min: 0.000001, 
      errorMessage: "Step must be a positive number" 
    }),
    
    // Price type support (same as limit orders)
    price_type: PriceTypeEnum.optional(),
    price_offset: z.union([
      z.string()
        .refine(val => val === '' || !isNaN(Number(val)), { 
          message: "Price offset must be a number" 
        })
        .transform(val => val === '' ? null : Number(val))
        .nullable()
        .optional(),
      z.number().nullable().optional(),
      z.null().optional()
    ]),
    price_reference: PriceReferenceEnum.optional(),
    
    // Distribution configuration
    distribution_type: z.string().optional().default('step_based'),
    center_on_price: z.boolean().optional().default(false),
    
    // Zone-based fields (for Phase 3)
    zone_bottom: numberOrStringNumber({ 
      min: 0.000001, 
      errorMessage: "Zone bottom must be a positive number" 
    }).optional(),
    zone_top: numberOrStringNumber({ 
      min: 0.000001, 
      errorMessage: "Zone top must be a positive number" 
    }).optional(),
    distribution_strategy: z.string().optional().default('even'),
}).nullable().optional();

// Main preset schema (maps to PresetCreateRequest)
export const presetSchema = z.object({
  name: z.string()
    .min(1, { message: "Preset name is required" })
    .min(2, { message: "Name must be at least 2 characters long" })
    .max(100, { message: "Name must be less than 100 characters" })
    .regex(/^[a-zA-Z0-9\s\-_]+$/, { 
      message: "Name can only contain letters, numbers, spaces, hyphens, and underscores" 
    }),
  description: z.string().nullable().optional(),
  symbol: z.string().min(1, { message: "Symbol is required" }),
  order_type: OrderTypeEnum,
  side: OrderSideEnum,
  amount: numberOrStringNumber({ 
    min: 0.000001, 
    errorMessage: "Amount must be a positive number" 
  }),
  amount_type: AmountTypeEnum,
  margin_mode: MarginModeEnum.optional(), // Optional in request
  leverage: numberOrStringNumber({ 
    min: 1, 
    max: 50, 
    errorMessage: "Leverage must be between 1 and 50" 
  }),
  limit_price: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Limit price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  limit_price_type: PriceTypeEnum.nullable().optional(),
  price_offset: z.union([
    z.string()
      .refine(val => val === '' || !isNaN(Number(val)), { 
        message: "Price offset must be a number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().nullable().optional(),
    z.null().optional()
  ]),
  price_reference: PriceReferenceEnum.nullable().optional(),
  stop_price: z.union([
    z.string()
      .refine(val => val === '' || (!isNaN(Number(val)) && Number(val) > 0), { 
        message: "Stop price must be a positive number" 
      })
      .transform(val => val === '' ? null : Number(val))
      .nullable()
      .optional(),
    z.number().positive().nullable().optional(),
    z.null().optional()
  ]),
  take_profit: TakeProfitSettingsSchema.optional(),
  stop_loss: StopLossSettingsSchema.optional(),
  layered: LayeredOrderSettingsSchema.optional(),
  flags: OrderFlagsSchema.optional(),
  webhook_enabled: z.boolean().optional(),
  webhook_id: z.string().min(1).nullable().optional(),
  webhook_secret: z.string().optional(),
  allow_override: z.boolean().optional().default(true),
  preset_type: z.literal('preset'), // Change to literal instead of enum
});

// Schema for linking presets within a TradeSet
const TradeSetPresetLinkSchema = z.object({
    // Preset IDs are UUID strings from the backend
    preset_id: z.string().uuid({ message: "Preset ID must be a valid UUID" }),
    order: numberOrStringNumber({ 
      min: 1, 
      errorMessage: "Order must be a positive number" 
    }).transform(val => Math.floor(Number(val))), // Ensure integer
    // Add conditions schema if needed later
});

// Main tradeSet schema (maps to TradeSetCreateRequest)
export const tradeSetSchema = z.object({
    name: z.string()
      .min(1, { message: "TradeSet name is required" })
      .min(2, { message: "Name must be at least 2 characters long" })
      .max(100, { message: "Name must be less than 100 characters" })
      .regex(/^[a-zA-Z0-9\s\-_]+$/, { 
        message: "Name can only contain letters, numbers, spaces, hyphens, and underscores" 
      }),
    symbol: z.string().min(1, { message: "Symbol is required" }), // Added symbol
    description: z.string().nullable().optional(),
    // Presets array: optional for creation via wizard, assuming linking happens later
    presets: z.array(TradeSetPresetLinkSchema).optional().default([]),
    webhook_enabled: z.boolean().optional(),
    // Fix: Allow empty string when webhook is disabled
    webhook_id: z.string().nullable().optional(),
    webhook_secret: z.string().optional(),
    allow_override: z.boolean().optional().default(true),
    preset_type: z.literal('tradeset'), // Ensure type is 'tradeset'
});

// Create a discriminated union based on the preset_type field
export const wizardSchema = z.discriminatedUnion("preset_type", [
  presetSchema,
  tradeSetSchema,
]);

// Separate schema for create operations (requires webhook_secret when webhook is enabled)
export const wizardSchemaForCreate = wizardSchema.refine(data => {
  // If webhook is not enabled, no validation needed
  if (!data.webhook_enabled) {
    return true;
  }
  
  // If webhook is enabled, both webhook_id and webhook_secret are required
  const hasValidId = data.webhook_id && data.webhook_id.trim().length > 0;
  const hasValidSecret = data.webhook_secret && data.webhook_secret.length >= 8;
  
  return hasValidId && hasValidSecret;
}, {
    // Custom validation: If webhook is enabled, ID and secret (min 8 chars) are required.
    message: "Webhook ID and secret (min 8 chars) are required when webhook is enabled",
    // Apply error to a specific field or make it a general form error
    path: ["webhook_id"], // Apply error to webhook_id field
});

// Schema for edit operations (webhook_secret is optional if webhook is already enabled)
export const wizardSchemaForEdit = wizardSchema.refine(data => {
  // If webhook is not enabled, no validation needed
  if (!data.webhook_enabled) {
    return true;
  }
  
  // If webhook is enabled, only ID is required (secret can be empty to keep existing)
  const hasValidId = data.webhook_id && data.webhook_id.trim().length > 0;
  return hasValidId;
}, {
    // Custom validation: If webhook is enabled, only ID is required (secret can be empty to keep existing)
    message: "Webhook ID is required when webhook is enabled",
    path: ["webhook_id"],
});

// Enhanced preset schema with async duplicate name validation
// Note: This should be used carefully as it makes async API calls
export const presetSchemaWithDuplicateCheck = presetSchema.refine(
  async (data) => {
    if (!data.name || data.name.length < 2) {
      return true; // Let other validations handle empty/short names
    }
    
    try {
      const exists = await presetService.checkPresetNameExists(data.name);
      return !exists; // Return false if preset exists (validation fails)
    } catch (error) {
      console.warn('Error checking preset name during validation:', error);
      return true; // Allow submission if check fails - backend will handle
    }
  },
  {
    message: "A preset with this name already exists. Please choose a different name.",
    path: ["name"],
  }
);

// Infer the combined type (optional but can be useful)
export type WizardFormData = z.infer<typeof wizardSchema>; 